import React, { useState } from 'react';
import { Chip, Typography, IconButton, Menu, MenuItem, ListItemText, Tooltip, Stack } from '@mui/material';
import { AddCircle, Close } from '@mui/icons-material';
import { ContextScopeModel, OperationsClient } from '../../../api/api';
import { useApiClient, useToastService } from '@@common';

interface OperationScopeChipsProps {
	operationId: string;
	operationName: string;
	assignedScopeIds: string[];
	availableScopes: ContextScopeModel[];
	onScopesUpdated: () => void;
}

const OperationScopeChips: React.FC<OperationScopeChipsProps> = ({ operationId, operationName, assignedScopeIds, availableScopes, onScopesUpdated }) => {
	const client = useApiClient(OperationsClient);
	const toastService = useToastService();
	const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
	const [loading, setLoading] = useState(false);

	// Get assigned scopes with their details
	const assignedScopes = availableScopes.filter((scope) => assignedScopeIds.includes(scope.id));

	// Get unassigned scopes that can be added
	const unassignedScopes = availableScopes.filter((scope) => !assignedScopeIds.includes(scope.id));

	const handleRemoveScope = async (scopeId: string, scopeName: string) => {
		setLoading(true);
		try {
			const newScopeIds = assignedScopeIds.filter((id) => id !== scopeId);
			await client.setScopes(operationId, newScopeIds);
			toastService.success(`Removed scope "${scopeName}" from operation "${operationName}"`);
			onScopesUpdated();
		} catch (error) {
			console.error('Error removing scope:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove scope: ${errorMessage}`);
		} finally {
			setLoading(false);
		}
	};

	const handleAddScope = async (scopeId: string, scopeName: string) => {
		setLoading(true);
		try {
			const newScopeIds = [...assignedScopeIds, scopeId];
			await client.setScopes(operationId, newScopeIds);
			toastService.success(`Added scope "${scopeName}" to operation "${operationName}"`);
			onScopesUpdated();
		} catch (error) {
			console.error('Error adding scope:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to add scope: ${errorMessage}`);
		} finally {
			setLoading(false);
		}
		setAddMenuAnchor(null);
	};

	const handleOpenAddMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAddMenuAnchor(event.currentTarget);
	};

	const handleCloseAddMenu = () => {
		setAddMenuAnchor(null);
	};

	if (availableScopes.length === 0) {
		return null;
	}

	return (
		<>
			<Stack direction="column" spacing={0.5} alignItems="flex-start">
				<Tooltip title="Required Authorization Scopes">
					<Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}>
						Auth Scopes
					</Typography>
				</Tooltip>

				<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
					{assignedScopes.length === 0 ? (
						<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
							None
						</Typography>
					) : (
						assignedScopes.map((scope) => (
							<Chip
								key={scope.id}
								label={scope.name}
								size="small"
								color="primary"
								variant="outlined"
								disabled={loading}
								onDelete={() => handleRemoveScope(scope.id, scope.name)}
								deleteIcon={
									<Tooltip title={`Remove scope "${scope.name}"`}>
										<Close fontSize="small" />
									</Tooltip>
								}
							/>
						))
					)}

					{unassignedScopes.length > 0 && (
						<Tooltip title="Add scope">
							<span>
								<IconButton
									size="small"
									onClick={handleOpenAddMenu}
									disabled={loading}
									sx={{
										border: '1px solid',
										borderColor: 'primary.main',
										width: 24,
										height: 24,
										'&:hover': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
									}}
								>
									<AddCircle fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
				</Stack>
			</Stack>

			<Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={handleCloseAddMenu} PaperProps={{ style: { maxHeight: 300, width: 280 } }}>
				<MenuItem disabled sx={{ fontWeight: 600, color: 'text.secondary' }}>
					Available Scopes
				</MenuItem>
				{unassignedScopes.map((scope) => (
					<MenuItem key={scope.id} onClick={() => handleAddScope(scope.id, scope.name)} disabled={loading}>
						<ListItemText
							primary={scope.name}
							secondary={scope.description}
							secondaryTypographyProps={{
								variant: 'caption',
								sx: {
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
								},
							}}
						/>
					</MenuItem>
				))}
			</Menu>
		</>
	);
};

export default OperationScopeChips;
